import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime, kg } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { CatchReport, FishingLicence, Page, QuotaAllocation, QuotaLedgerEntry } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const FAO_SPECIES = [
  { code: 'YFT', name: 'Yellowfin tuna' },
  { code: 'SKJ', name: 'Skipjack tuna' },
  { code: 'BET', name: 'Bigeye tuna' },
  { code: 'ALB', name: 'Albacore tuna' },
  { code: 'SWO', name: 'Swordfish' },
  { code: 'BLM', name: 'Black marlin' },
  { code: 'DOL', name: 'Common dolphinfish (mahi-mahi)' },
  { code: 'SNA', name: 'Snappers nei (job fish)' },
  { code: 'GPX', name: 'Groupers nei' },
  { code: 'LOB', name: 'Spiny lobsters nei' },
  { code: 'OCT', name: 'Octopus' },
] as const;
const GEAR_TYPES = ['HANDLINE', 'TROLLING', 'LONGLINE', 'GILLNET', 'FISH_TRAP', 'PURSE_SEINE', 'DIVING_SPEARGUN', 'POLE_AND_LINE'] as const;
const LIST_ROLES = ['SFA_LICENSING', 'QUOTA_MANAGER', 'OBSERVER_COORDINATOR'];

type Tab = 'catch' | 'quota';

/** M32 Fisheries — catch reporting (mobile-first, 90-second entry) and the species/zone quota ledger. */
export function CatchQuota() {
  const [tab, setTab] = useState<Tab>('catch');
  return (
    <div>
      <PageHeader title="Catch Reports & Quota" subtitle="Trip catch data and the species/zone quota allocation ledger (M32)." />
      <TabStrip tabs={[{ key: 'catch', label: 'Catch Reports' }, { key: 'quota', label: 'Quota Allocations' }]} active={tab} onChange={setTab} />
      {tab === 'catch' ? <CatchTab /> : <QuotaTab />}
    </div>
  );
}

// ── Catch reports ────────────────────────────────────────────────────────

function CatchTab() {
  const { persona } = useAuth();
  const isObserver = persona ? persona.roles.includes('OBSERVER_COORDINATOR') : false;
  const canList = persona ? persona.roles.some((r) => LIST_ROLES.includes(r)) : false;
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    return `/api/v1/m32/catch-reports?${params.toString()}`;
  }, [status]);
  const listQ = useApiQuery<Page<CatchReport>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {['SUBMITTED', 'VERIFIED', 'FLAGGED'].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <button className="btn-primary" onClick={() => setShowCreate(true)}>
          + Log catch report
        </button>
      </div>
      {!canList ? <RoleNotice roles={LIST_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No catch reports match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Report ref</th>
                <th className="table-head">Vessel</th>
                <th className="table-head">Zone</th>
                <th className="table-head">Trip date</th>
                <th className="table-head">Total weight</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((r) => (
                <tr key={r.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(r.id)}>
                  <td className="table-cell font-mono text-xs font-medium">{r.reportRef}</td>
                  <td className="table-cell font-mono text-xs">{r.vesselRegNo}</td>
                  <td className="table-cell text-xs text-slate-500">{r.zoneCode}</td>
                  <td className="table-cell text-xs text-slate-500">{formatDate(r.tripDate)}</td>
                  <td className="table-cell text-xs">{kg(r.totalWeightKg)}</td>
                  <td className="table-cell">
                    <StatusBadge status={r.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <CatchDetailModal
          id={selectedId}
          isObserver={isObserver}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            setSelectedId(null);
            listQ.reload();
          }}
        />
      ) : null}
      {showCreate ? (
        <NewCatchReportModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CatchDetailModal({ id, isObserver, onClose, onChanged }: { id: string; isObserver: boolean; onClose: () => void; onChanged: () => void }) {
  const rq = useApiQuery<CatchReport>(`/api/v1/m32/catch-reports/${id}`);
  const r = rq.data;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFlag, setShowFlag] = useState(false);
  const [reason, setReason] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };
  const verify = () => call(() => api.post(`/api/v1/m32/catch-reports/${id}/verify`));
  const flag = () => call(() => api.post(`/api/v1/m32/catch-reports/${id}/flag`, { reason }));

  return (
    <Modal title={r ? r.reportRef : 'Catch report'} onClose={onClose} wide>
      {rq.error ? <ErrorNote message={rq.error} /> : null}
      {rq.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {r ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={r.status} />} />
            <Detail label="Vessel" value={r.vesselRegNo} mono />
            <Detail label="Zone" value={r.zoneCode} />
            <Detail label="Method" value={r.method.replaceAll('_', ' ')} />
            <Detail label="Trip date" value={formatDate(r.tripDate)} />
            <Detail label="Landing port" value={r.landingPort} />
            <Detail label="Source" value={r.source.replaceAll('_', ' ')} />
            <Detail label="Recorded" value={formatDateTime(r.recordedAt)} />
            <Detail label="Synced" value={formatDateTime(r.syncedAt)} />
            <Detail label="Total weight" value={kg(r.totalWeightKg)} />
          </div>
          {r.flagReason ? <ErrorNote message={`Flagged: ${r.flagReason}`} /> : null}
          <div>
            <div className="field-label">Species lines</div>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="table-head">Species</th>
                  <th className="table-head">Estimated kg</th>
                </tr>
              </thead>
              <tbody>
                {r.lines.map((l) => (
                  <tr key={l.id}>
                    <td className="table-cell">{l.speciesCode}</td>
                    <td className="table-cell">{kg(l.estimatedKg)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {isObserver && r.status === 'SUBMITTED' ? (
            <div className="border-t border-slate-100 pt-4">
              {!showFlag ? (
                <div className="flex gap-2">
                  <button className="btn-success" disabled={busy} onClick={verify}>
                    Mark verified
                  </button>
                  <button className="btn-danger" disabled={busy} onClick={() => setShowFlag(true)}>
                    Flag
                  </button>
                </div>
              ) : (
                <div className="space-y-2 rounded-lg border border-slate-200 p-3">
                  <Field label="Flag reason">
                    <textarea className="field-input" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
                  </Field>
                  <div className="flex gap-2">
                    <button className="btn-secondary" onClick={() => setShowFlag(false)}>
                      Cancel
                    </button>
                    <button className="btn-danger" disabled={busy || reason.trim().length < 3} onClick={flag}>
                      Confirm flag
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function LicencePicker({ value, onPick }: { value: FishingLicence | null; onPick: (l: FishingLicence) => void }) {
  const [q, setQ] = useState('');
  const path = useMemo(() => (q.length >= 2 ? `/api/v1/m32/licences?limit=8&q=${encodeURIComponent(q)}` : null), [q]);
  const results = useApiQuery<Page<FishingLicence>>(path);

  if (value) {
    return (
      <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
        <span>
          <span className="font-mono font-medium">{value.licenceNo}</span> — {value.vesselName}
        </span>
        <button className="text-xs font-semibold text-brand-700 underline" onClick={() => onPick(null as unknown as FishingLicence)}>
          Change
        </button>
      </div>
    );
  }
  return (
    <div>
      <input className="field-input" placeholder="Search licence no or vessel reg…" value={q} onChange={(e) => setQ(e.target.value)} />
      {results.data && results.data.items.length > 0 ? (
        <div className="mt-1 max-h-40 overflow-y-auto rounded-lg border border-slate-200">
          {results.data.items.map((l) => (
            <button key={l.id} className="block w-full px-3 py-1.5 text-left text-xs hover:bg-slate-50" onClick={() => onPick(l)}>
              <span className="font-mono font-medium">{l.licenceNo}</span> — {l.vesselName} ({l.vesselRegNo})
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function NewCatchReportModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [licence, setLicence] = useState<FishingLicence | null>(null);
  const [zoneCode, setZoneCode] = useState('');
  const [method, setMethod] = useState<(typeof GEAR_TYPES)[number]>('HANDLINE');
  const [tripDate, setTripDate] = useState(new Date().toISOString().slice(0, 10));
  const [landingPort, setLandingPort] = useState('Victoria Fishing Port');
  const [lines, setLines] = useState<Array<{ speciesCode: string; estimatedKg: string }>>([{ speciesCode: 'YFT', estimatedKg: '' }]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateLine = (i: number, patch: Partial<{ speciesCode: string; estimatedKg: string }>) =>
    setLines(lines.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const submit = async () => {
    if (!licence) return;
    setBusy(true);
    setError(null);
    try {
      const now = new Date().toISOString();
      await api.post('/api/v1/m32/catch-reports', {
        fishingLicenceId: licence.id,
        zoneCode,
        method,
        tripDate,
        recordedAt: now,
        landingPort,
        source: 'OFFICER_ENTRY',
        lines: lines.filter((l) => l.estimatedKg).map((l) => ({ speciesCode: l.speciesCode, estimatedKg: Number.parseFloat(l.estimatedKg) })),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Log catch report" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="Fishing licence">
          <LicencePicker value={licence} onPick={setLicence} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Zone code">
            <input className="field-input" value={zoneCode} onChange={(e) => setZoneCode(e.target.value)} placeholder="e.g. MAHE_PLATEAU" />
          </Field>
          <Field label="Gear/method">
            <select className="field-input" value={method} onChange={(e) => setMethod(e.target.value as (typeof GEAR_TYPES)[number])}>
              {GEAR_TYPES.map((g) => (
                <option key={g} value={g}>
                  {g.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Trip date">
            <input className="field-input" type="date" value={tripDate} onChange={(e) => setTripDate(e.target.value)} />
          </Field>
        </div>
        <Field label="Landing port">
          <input className="field-input" value={landingPort} onChange={(e) => setLandingPort(e.target.value)} />
        </Field>
        <div>
          <div className="field-label">Species &amp; estimated weight</div>
          <div className="space-y-2">
            {lines.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                <select className="field-input !w-56" value={l.speciesCode} onChange={(e) => updateLine(i, { speciesCode: e.target.value })}>
                  {FAO_SPECIES.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.name} ({s.code})
                    </option>
                  ))}
                </select>
                <input
                  className="field-input"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="kg"
                  value={l.estimatedKg}
                  onChange={(e) => updateLine(i, { estimatedKg: e.target.value })}
                />
                {lines.length > 1 ? (
                  <button className="text-xs text-rose-600" onClick={() => setLines(lines.filter((_, idx) => idx !== i))}>
                    Remove
                  </button>
                ) : null}
              </div>
            ))}
            <button className="text-xs font-semibold text-brand-700" onClick={() => setLines([...lines, { speciesCode: 'YFT', estimatedKg: '' }])}>
              + Add species line
            </button>
          </div>
        </div>
        <button
          className="btn-primary w-full"
          disabled={busy || !licence || !zoneCode || !landingPort || lines.every((l) => !l.estimatedKg)}
          onClick={submit}
        >
          {busy ? 'Submitting…' : 'Submit catch report'}
        </button>
      </div>
    </Modal>
  );
}

// ── Quota allocations & ledger ────────────────────────────────────────────

function QuotaTab() {
  const { persona } = useAuth();
  const isQuotaManager = persona ? persona.roles.includes('QUOTA_MANAGER') : false;
  const [periodYear, setPeriodYear] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (periodYear) params.set('periodYear', periodYear);
    return `/api/v1/m32/quota/allocations?${params.toString()}`;
  }, [periodYear]);
  const listQ = useApiQuery<Page<QuotaAllocation>>(path);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <input className="field-input !w-40" type="number" placeholder="Period year" value={periodYear} onChange={(e) => setPeriodYear(e.target.value)} />
        {isQuotaManager ? (
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New allocation
          </button>
        ) : null}
      </div>
      {!isQuotaManager ? <RoleNotice roles={['QUOTA_MANAGER']} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No quota allocations match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Species</th>
                <th className="table-head">Zone</th>
                <th className="table-head">Period</th>
                <th className="table-head">Allocated</th>
                <th className="table-head">Balance</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(a.id)}>
                  <td className="table-cell font-medium">{a.speciesCode}</td>
                  <td className="table-cell text-xs text-slate-500">{a.zoneCode}</td>
                  <td className="table-cell text-xs">{a.periodYear}</td>
                  <td className="table-cell text-xs">{kg(a.allocatedKg)}</td>
                  <td className={`table-cell text-xs font-semibold ${a.balanceKg < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{kg(a.balanceKg)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {selectedId ? (
        <AllocationDetailModal
          id={selectedId}
          isQuotaManager={isQuotaManager}
          onClose={() => setSelectedId(null)}
          onChanged={() => {
            listQ.reload();
          }}
        />
      ) : null}
      {showCreate ? (
        <NewAllocationModal
          onClose={() => setShowCreate(false)}
          onCreated={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function AllocationDetailModal({ id, isQuotaManager, onClose, onChanged }: { id: string; isQuotaManager: boolean; onClose: () => void; onChanged: () => void }) {
  const aq = useApiQuery<QuotaAllocation>(`/api/v1/m32/quota/allocations/${id}`);
  const ledgerQ = useApiQuery<Page<QuotaLedgerEntry>>(`/api/v1/m32/quota/allocations/${id}/ledger?limit=50`);
  const a = aq.data;
  const [deltaKg, setDeltaKg] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const adjust = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/m32/quota/allocations/${id}/adjust`, { deltaKg: Number.parseFloat(deltaKg), note });
      setDeltaKg('');
      setNote('');
      aq.reload();
      ledgerQ.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={a ? `${a.speciesCode} — ${a.zoneCode} (${a.periodYear})` : 'Quota allocation'} onClose={onClose} wide>
      {aq.error ? <ErrorNote message={aq.error} /> : null}
      {aq.loading ? <Spinner /> : null}
      {a ? (
        <div className="space-y-5">
          <div className="grid grid-cols-3 gap-3">
            <Detail label="Allocated" value={kg(a.allocatedKg)} />
            <Detail label="Balance" value={kg(a.balanceKg)} />
            <Detail label="Notes" value={a.notes ?? '—'} />
          </div>
          <div>
            <div className="field-label">Ledger entries</div>
            {ledgerQ.loading ? (
              <Spinner />
            ) : (ledgerQ.data?.items.length ?? 0) === 0 ? (
              <p className="text-sm text-slate-400">No ledger entries yet.</p>
            ) : (
              <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="table-head">Kind</th>
                      <th className="table-head">Delta</th>
                      <th className="table-head">Balance after</th>
                      <th className="table-head">Note</th>
                      <th className="table-head">When</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ledgerQ.data!.items.map((e) => (
                      <tr key={e.id}>
                        <td className="table-cell">
                          <StatusBadge status={e.kind} />
                        </td>
                        <td className={`table-cell font-mono ${Number(e.deltaKg) < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>{kg(e.deltaKg)}</td>
                        <td className="table-cell font-mono">{kg(e.balanceAfterKg)}</td>
                        <td className="table-cell text-xs text-slate-500">{e.note ?? '—'}</td>
                        <td className="table-cell text-xs text-slate-500">{formatDateTime(e.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {isQuotaManager ? (
            <div className="space-y-2 border-t border-slate-100 pt-4">
              {error ? <ErrorNote message={error} /> : null}
              <div className="field-label">Manual adjustment</div>
              <div className="flex gap-2">
                <input className="field-input !w-32" type="number" step="0.1" placeholder="± kg" value={deltaKg} onChange={(e) => setDeltaKg(e.target.value)} />
                <input className="field-input flex-1" placeholder="Reason for adjustment" value={note} onChange={(e) => setNote(e.target.value)} />
                <button className="btn-primary" disabled={busy || !deltaKg || note.trim().length < 3} onClick={adjust}>
                  {busy ? 'Applying…' : 'Apply'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}

function NewAllocationModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [speciesCode, setSpeciesCode] = useState<(typeof FAO_SPECIES)[number]['code']>('YFT');
  const [zoneCode, setZoneCode] = useState('');
  const [periodYear, setPeriodYear] = useState(String(new Date().getFullYear()));
  const [allocatedKg, setAllocatedKg] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m32/quota/allocations', {
        speciesCode,
        zoneCode,
        periodYear: Number.parseInt(periodYear, 10),
        allocatedKg: Number.parseFloat(allocatedKg),
        notes: notes || undefined,
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New quota allocation" onClose={onClose}>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="space-y-3">
        <Field label="Species">
          <select className="field-input" value={speciesCode} onChange={(e) => setSpeciesCode(e.target.value as (typeof FAO_SPECIES)[number]['code'])}>
            {FAO_SPECIES.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name} ({s.code})
              </option>
            ))}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Zone code">
            <input className="field-input" value={zoneCode} onChange={(e) => setZoneCode(e.target.value)} placeholder="e.g. OFFSHORE_EEZ_N" />
          </Field>
          <Field label="Period year">
            <input className="field-input" type="number" value={periodYear} onChange={(e) => setPeriodYear(e.target.value)} />
          </Field>
        </div>
        <Field label="Allocated kg">
          <input className="field-input" type="number" step="0.1" value={allocatedKg} onChange={(e) => setAllocatedKg(e.target.value)} />
        </Field>
        <Field label="Notes (optional)">
          <textarea className="field-input" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
        <button className="btn-primary w-full" disabled={busy || !zoneCode || !allocatedKg} onClick={submit}>
          {busy ? 'Creating…' : 'Create allocation'}
        </button>
      </div>
    </Modal>
  );
}
